<template>
  <div class="tw:flex tw:flex-col tw:gap-2">
    <span class="tw:text-13 tw:font-medium tw:text-gray-700">{{ $t('registration.company.logo') }}</span>

    <!-- a button so the keyboard reaches it; the drag handlers share the same drop target -->
    <!-- lithium's unlayered reboot resets button border-radius, so rounded-lg needs `!` -->
    <button
      type="button"
      class="tw:flex tw:items-center tw:gap-3 tw:rounded-lg! tw:border tw:border-dashed tw:bg-gray-50 tw:p-3.5 tw:text-left"
      :class="logoBorderClass"
      data-testid="reg-company-logo"
      @click="openFilePicker"
      @dragenter.prevent="isDraggingOver = true"
      @dragover.prevent="isDraggingOver = true"
      @dragleave.prevent="isDraggingOver = false"
      @drop.prevent="onFileDropped">
      <img
        v-if="logoPreview"
        :src="logoPreview"
        alt=""
        class="tw:size-11 tw:rounded-lg tw:border tw:border-gray-200 tw:bg-white tw:object-contain" />
      <span
        v-else
        class="tw:flex tw:size-11 tw:items-center tw:justify-center tw:rounded-lg tw:border tw:border-gray-200 tw:bg-white tw:text-muted">
        <IconPhoto :size="20" stroke-width="1.5" />
      </span>
      <span class="tw:flex tw:min-w-0 tw:flex-col tw:gap-0.5">
        <span class="tw:text-13 tw:font-medium tw:text-gray-900">{{ logoLabel }}</span>
        <span class="tw:text-12 tw:text-muted">{{ $t('registration.company.logo_limit') }}</span>
      </span>
    </button>

    <input
      ref="fileInputRef"
      type="file"
      accept="image/png,image/svg+xml,image/webp"
      class="tw:hidden"
      data-testid="reg-company-logo-input"
      @change="onFileChosen" />

    <span
      v-if="logoError"
      class="tw:text-13 tw:text-danger tw:text-nowrap"
      role="alert"
      data-testid="reg-company-logo-error">
      {{ logoError }}
    </span>
  </div>
</template>

<script setup lang="ts">
/** Logo upload (PNG, WebP or SVG, max 2 MB); SVGs are rasterized server-side at a density derived from their size. */
import { IconPhoto } from '@tabler/icons-vue';
import { computed, ref, useTemplateRef, watch } from 'vue';

import { registrationClient } from '@/api/registration';
import { registrationErrorMessage } from '@/components/registration/subcomponents/registrationErrors';
import translator from '@/locale/translator';
import { detectSvgDensity } from '@/utils/svgDensity';

const SVG_TYPE = 'image/svg+xml';
const ACCEPTED_LOGO_TYPES = ['image/png', 'image/webp', SVG_TYPE];
/** 2 MB decimal, matching the backend limit */
const MAX_LOGO_BYTES = 2_000_000;

/* ----------------------------------- STATE ---------------------------------- */
const logoId = defineModel<number | null>('logoId', { required: true });

/** thumbnail of a logo hydrated from a previous visit; arrives after mount */
const props = defineProps<{ existingLogoUrl?: string | null }>();

const logoError = ref('');
const isUploading = ref(false);
const isDraggingOver = ref(false);
/** preview url from the last upload or hydration; the draft only carries the id */
const logoPreview = ref('');
const fileInputRef = useTemplateRef<HTMLInputElement>('fileInputRef');

/* --------------------------------- COMPUTED --------------------------------- */
const logoLabel = computed(() => {
  if (isUploading.value) return translator.get('registration.company.logo_uploading');
  return translator.get(logoId.value ? 'registration.company.logo_replace' : 'registration.company.logo_drop');
});

const logoBorderClass = computed(() => {
  if (logoError.value) return 'tw:border-danger';
  return isDraggingOver.value ? 'tw:border-primary' : 'tw:border-gray-300';
});

/* --------------------------------- METHODS ---------------------------------- */
function openFilePicker() {
  fileInputRef.value?.click();
}

async function onFileChosen(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  // reset so choosing the same file again still fires a change event
  input.value = '';
  if (!file) return;

  await handleChosenFile(file);
}

async function onFileDropped(event: DragEvent) {
  isDraggingOver.value = false;

  const file = event.dataTransfer?.files?.[0];
  if (!file) return;

  await handleChosenFile(file);
}

async function handleChosenFile(file: File) {
  const error = await validateLogo(file);
  if (error) {
    logoError.value = error;
    return;
  }

  const density = file.type === SVG_TYPE ? detectSvgDensity(await file.text()) : undefined;
  uploadLogo(file, density);
}

async function validateLogo(file: File): Promise<string> {
  if (!ACCEPTED_LOGO_TYPES.includes(file.type)) return translator.get('registration.company.logo_error_type');
  if (file.size > MAX_LOGO_BYTES) return translator.get('registration.company.logo_error_size');
  if (file.type === SVG_TYPE && !(await isWellFormedSvg(file))) {
    return translator.get('registration.company.logo_error_svg');
  }
  return '';
}

/** rejects malformed SVGs (parse error or non-svg root) before upload */
async function isWellFormedSvg(file: File): Promise<boolean> {
  const markup = await file.text();
  const document = new DOMParser().parseFromString(markup, SVG_TYPE);
  return !document.querySelector('parsererror') && document.documentElement.nodeName.toLowerCase() === 'svg';
}

async function uploadLogo(file: File, pixelDensity?: number) {
  if (isUploading.value) return;

  isUploading.value = true;
  logoError.value = '';

  try {
    const response = await registrationClient.uploadLogo(file, pixelDensity);
    logoId.value = response.id;
    // the proxy may not serve the file yet, so fall back to the local copy
    logoPreview.value = response.url || URL.createObjectURL(file);
  } catch (error) {
    logoError.value = registrationErrorMessage(error, translator.get('registration.company.logo_error_upload'));
  } finally {
    isUploading.value = false;
  }
}

/* --------------------------------- WATCHERS --------------------------------- */
// hydration resolves after mount; don't override a file the registrant already picked
watch(
  () => props.existingLogoUrl,
  (url) => {
    if (url && !logoPreview.value) logoPreview.value = url;
  },
  { immediate: true }
);
</script>
